<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    tabindex="-1"
    v-if="isVisible"
  >
    <div :class="modalDialogClass">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i :class="modalHeaderIconClass + ' me-2'"></i>
            {{ $t(modalTitleKey) }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <p v-if="modalHintKey" class="text-muted small mb-3">
            {{ $t(modalHintKey) }}
          </p>
          <p
            v-else-if="
              collectAmountCalculatorVisible &&
                allowAmountEdit &&
                isCollectTarget
            "
            class="text-muted small mb-2"
          >
            {{ $t("financialSummary.quickAddCollectHintWithCalc") }}
          </p>
          <p v-else class="text-muted small mb-3">
            {{
              allowAmountEdit
                ? $t("financialSummary.quickAddCollectHintEditable")
                : $t("financialSummary.quickAddCollectHint")
            }}
          </p>
          <div
            v-if="
              isCollectTarget &&
              formattedCollectAmount !== null &&
              !allowAmountEdit
            "
            class="card bg-light border-primary mb-3"
          >
            <div
              class="card-body py-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
            >
              <span class="text-muted small mb-0">{{
                $t("financialSummary.quickAddCollectAmountLabel")
              }}</span>
              <span class="h5 mb-0 text-primary">
                ${{ formattedCollectAmount }}
              </span>
            </div>
          </div>
          <ContentSkeleton v-if="loading" variant="form" :rows="5" padded />
          <div v-else-if="quickFields.length > 0">
            <div
              v-if="collectAmountCalculatorVisible"
              class="card border mb-3 quick-collect-calc"
            >
              <div class="card-body py-2 px-3">
                <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span class="small fw-semibold text-uppercase text-muted mb-0" style="letter-spacing: 0.04em">
                    <i class="fas fa-cubes me-1 text-secondary" aria-hidden="true"></i>
                    {{ $t("financialSummary.quickAddCollectCalcCompactTitle") }}
                  </span>
                </div>
                <ul class="list-unstyled mb-0">
                  <li
                    v-for="(meta, idx) in collectLineMetas"
                    v-show="meta.qty > 0"
                    :key="'qc-line-' + idx + '-' + (meta.line && meta.line._id ? meta.line._id : idx)"
                    class="d-flex flex-wrap align-items-center gap-2 py-2 border-bottom border-light"
                  >
                    <div class="flex-grow-1 min-w-0">
                      <div
                        class="small fw-semibold text-body"
                        style="font-size: 0.8rem; line-height: 1.35; word-break: break-word"
                        :title="invoiceLineProductsJoined(meta.line, idx)"
                      >
                        {{ invoiceLineProductsJoined(meta.line, idx) }}
                      </div>
                      <div class="d-flex flex-wrap align-items-center gap-1 mt-1">
                        <span
                          class="badge rounded-pill fw-normal border bg-white text-secondary px-2"
                          style="font-size: 0.65rem"
                          :title="
                            meta.priceIsPerContainer
                              ? $t('financialSummary.quickAddCollectCalcModePerCTooltip')
                              : $t('financialSummary.quickAddCollectCalcModeLineTooltip')
                          "
                        >
                          {{
                            meta.priceIsPerContainer
                              ? $t("financialSummary.quickAddCollectCalcBadgePerC")
                              : $t("financialSummary.quickAddCollectCalcBadgeLine")
                          }}
                        </span>
                        <span class="text-muted tabular-nums" style="font-size: 0.7rem">
                          {{
                            $t("financialSummary.quickAddCollectCalcLineSub", {
                              each: formatCalcMoney(meta.perContainer),
                              max: meta.qty,
                            })
                          }}
                        </span>
                      </div>
                    </div>
                    <input
                      :id="'qc-pl-' + idx"
                      type="number"
                      min="0"
                      :max="meta.qty"
                      step="1"
                      class="form-control form-control-sm"
                      style="width: 3.5rem"
                      :value="perLinePicks[idx] || 0"
                      @input="onPerLinePickInput($event, idx, meta.qty)"
                    />
                  </li>
                </ul>
                <p v-if="collectCalcCapShort" class="text-muted mb-0 mt-2" style="font-size: 0.7rem">
                  {{ collectCalcCapShort }}
                </p>
                <div
                  class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top border-light"
                >
                  <label class="small fw-semibold text-body mb-0" for="qc-amount-display">
                    {{ $t("financialSummary.quickAddCollectAmountLabel") }} *
                  </label>
                  <span id="qc-amount-display" class="fs-5 fw-semibold text-primary tabular-nums">
                    {{ formatCalcMoney(quickCollectAmount) }}
                  </span>
                </div>
                <p
                  v-if="collectCalcAmountCappedByBalance && collectCalcUsesAdvanceSliceOnSelection"
                  class="text-muted small mb-0 mt-2"
                  style="font-size: 0.75rem"
                >
                  {{
                    $t("financialSummary.quickAddCollectCalcCappedAdvancePool", {
                      pct: formatAdvancePctLabel(resolvedQuickCollectAdvancePct),
                      invoice: formatCalcMoney(perLineSuggestedRaw),
                      advance: formatCalcMoney(collectCalcNominalCollectFromSelection),
                      cap: formatCalcMoney(quickCollectAmount),
                    })
                  }}
                </p>
                <p
                  v-else-if="collectCalcAmountCappedByBalance"
                  class="text-muted small mb-0 mt-2"
                  style="font-size: 0.75rem"
                >
                  {{
                    $t("financialSummary.quickAddCollectCalcCappedByBalance", {
                      selected: formatCalcMoney(perLineSuggestedRaw),
                      cap: formatCalcMoney(quickCollectAmount),
                    })
                  }}
                </p>
                <p
                  v-else-if="collectCalcUsesAdvanceSliceOnSelection && collectCalcTotalPicks > 0"
                  class="text-muted small mb-0 mt-2"
                  style="font-size: 0.75rem"
                >
                  {{
                    $t("financialSummary.quickAddCollectCalcAdvanceBreakdown", {
                      pct: formatAdvancePctLabel(resolvedQuickCollectAdvancePct),
                      invoice: formatCalcMoney(perLineSuggestedRaw),
                      amount: formatCalcMoney(quickCollectAmount),
                    })
                  }}
                </p>
                <p
                  v-else-if="collectCalcTotalPicks === 0 && collectCalcUsesAdvanceSliceOnSelection"
                  class="text-muted small mb-0 mt-2"
                  style="font-size: 0.75rem"
                >
                  {{
                    $t("financialSummary.quickAddCollectCalcNoPicksUsesPendingAdvance", {
                      pct: formatAdvancePctLabel(resolvedQuickCollectAdvancePct),
                    })
                  }}
                </p>
                <p
                  v-else-if="collectCalcTotalPicks === 0"
                  class="text-muted small mb-0 mt-2"
                  style="font-size: 0.75rem"
                >
                  {{ $t("financialSummary.quickAddCollectCalcNoPicksUsesPending") }}
                </p>
              </div>
            </div>
            <div v-if="isCollectTarget" class="mb-3">
              <label class="form-label small mb-1" for="qac-collection-date">{{
                $t("financialSummary.stages.quickEditCollectDateLabel")
              }}</label>
              <input
                id="qac-collection-date"
                v-model="collectionDateDraft"
                type="date"
                class="form-control form-control-sm"
                :disabled="isSubmitting"
              />
            </div>
            <div class="quick-collect-fields">
              <Form2
                :key="'qac-form-' + (collectAmountCalculatorVisible ? 'calc' : 'std')"
                ref="collectFieldsForm"
                :fields="quickFields"
                :value="formData"
                :lists="lists"
                :models="models"
                :modelDefinitions="modelDefinitions"
                :selectedModel="targetModel"
                :visualGroups="[]"
                :parentID="parentID"
                :showButtons="false"
                @input="onFormInput"
              />
            </div>
            <div
              v-if="collectAppliedtoExtrasVisible"
              class="card border-light mt-3 mb-0"
            >
              <div class="card-body py-2 px-3">
                <div class="row g-2">
                  <div class="col-md-6">
                    <label
                      class="form-label small mb-1"
                      for="qac-appliedto-rounding"
                      >{{ $t("financialSummary.quickAddCollectAppliedtoRounding") }}</label>
                    <input
                      id="qac-appliedto-rounding"
                      type="number"
                      step="0.01"
                      min="0"
                      class="form-control form-control-sm"
                      :value="appliedtoExtraRoundingDisplay"
                      @input="onAppliedtoExtraRoundingInput"
                    />
                  </div>
                  <div class="col-md-6">
                    <label
                      class="form-label small mb-1"
                      for="qac-appliedto-commission"
                      >{{ $t("financialSummary.quickAddCollectAppliedtoCommission") }}</label>
                    <input
                      id="qac-appliedto-commission"
                      type="number"
                      step="0.01"
                      min="0"
                      class="form-control form-control-sm"
                      :value="appliedtoExtraCommissionDisplay"
                      @input="onAppliedtoExtraCommissionInput"
                    />
                  </div>
                  <div
                    v-if="collectAmountWithRoundingVisible"
                    class="col-12 mt-1 pt-2 border-top d-flex justify-content-between align-items-center flex-wrap gap-1"
                  >
                    <span class="small text-muted mb-0">{{
                      $t("financialSummary.quickAddCollectAmountPlusRoundingTotal")
                    }}</span>
                    <span class="fw-semibold text-primary mb-0">
                      ${{ formattedCollectAmountWithRounding }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="showShippingInvoiceLinesEditor"
              class="quick-shipping-invoice-lines card border mt-3 mb-0 shadow-sm"
            >
              <div
                class="card-header py-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2 bg-white border-bottom"
              >
                <span class="small fw-bold text-uppercase text-muted mb-0" style="letter-spacing: 0.04em">
                  <i class="fas fa-file-invoice me-1 text-secondary" aria-hidden="true"></i>
                  {{ $t("financialSummary.quickAddShippingInvoiceLinesTitle") }}
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!canRedistributeInvoiceLineContainers"
                  @click="redistributeInvoiceLineContainers"
                >
                  <i class="fas fa-balance-scale me-1" aria-hidden="true"></i>
                  {{ $t("financialSummary.quickAddShippingInvoiceLinesAutoSplit") }}
                </button>
              </div>
              <div class="card-body py-2 px-3">
                <p class="text-muted small mb-2 mb-md-3">
                  {{ $t("financialSummary.quickAddShippingInvoiceLinesHint") }}
                </p>
                <ul class="list-unstyled mb-0 quick-shipping-invoice-lines__list">
                  <li
                    v-for="(line, idx) in shippingProductandpriceLines"
                    :key="'qspp-' + idx + '-' + (line._id || idx)"
                    class="quick-shipping-invoice-lines__row d-flex flex-wrap align-items-center gap-2 py-2 border-bottom border-light"
                  >
                    <div class="flex-grow-1 min-w-0">
                      <div class="small fw-semibold text-body text-truncate" :title="invoiceLineTitle(line, idx)">
                        {{ invoiceLineTitle(line, idx) }}
                      </div>
                      <div v-if="invoiceLineSubtitle(line)" class="text-muted" style="font-size: 0.65rem">
                        {{ invoiceLineSubtitle(line) }}
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                      <label class="small text-muted mb-0 text-nowrap" :for="'qspp-qty-' + idx">{{
                        $t("financialSummary.quickAddShippingInvoiceLineQtyLabel")
                      }}</label>
                      <input
                        :id="'qspp-qty-' + idx"
                        type="number"
                        min="0"
                        :max="lineQtyInputMax(idx)"
                        step="1"
                        class="form-control form-control-sm quick-shipping-invoice-lines__qty"
                        :value="invoiceLineQtyDisplay(line)"
                        @input="onInvoiceLineQtyInput($event, line, idx)"
                      />
                    </div>
                  </li>
                </ul>
                <div
                  class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top border-light"
                >
                  <span
                    class="small mb-0"
                    :class="invoiceQtySumMatches ? 'text-success' : 'text-danger'"
                  >
                    {{
                      $t("financialSummary.quickAddShippingInvoiceLinesSum", {
                        sum: invoiceQtySum,
                        ship: shippingQuickQtycontainers != null ? shippingQuickQtycontainers : "—",
                      })
                    }}
                  </span>
                  <span v-if="!invoiceQtySumMatches" class="small text-danger mb-0">
                    {{ $t("financialSummary.quickAddShippingInvoiceLinesMismatch") }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="alert alert-warning mb-0">
            {{ $t(missingFieldsMessageKey) }}
          </div>
        </div>
        <div
          v-if="isCollectTarget && !loading && quickFields.length > 0"
          class="modal-body border-top pt-3 pb-2"
        >
          <label class="form-label small mb-1" for="qac-collect-comment">{{
            $t("Comment")
          }}</label>
          <textarea
            id="qac-collect-comment"
            v-model="formData.comment"
            class="form-control form-control-sm"
            rows="2"
            :disabled="isSubmitting"
          ></textarea>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="
              loading ||
                isSubmitting ||
                quickFields.length === 0 ||
                (showShippingInvoiceLinesEditor && !invoiceQtySumMatches)
            "
            @click="submit"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("financialSummary.quickAddCollectSave") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi, getCurrentClientDbName } from "@/api/wencesApi";
import {
  confirmDuntowerCollectSaveWithoutFile,
  isCollectFormFileEmpty,
  isHookValidationError,
  promptHookValidationForceSave,
  showForceSaveRetryError,
} from "@/utils/dynamicModelValidationSwal";

import Swal from "sweetalert2";
import Form2 from "./Form2.vue";
import {
  processReferenceFormData,
  removeReferenceGroupIds,
} from "./referenceModalFormProcessing.js";
import { formatNumberSync, roundNumberBySettings } from "@/utils/numberFormatter";
import { calendarTodayLocalYyyyMmDd, toYyyyMmDdSync } from "@/utils/dateFormatter";
import {
  normalizeProductandpriceLines,
  lineEconomics,
  sumPerLinePicks,
} from "@/utils/quickCollectProformAmounts.js";
import {
  resolveProformContainerCountFromRecord,
  collectUsedContainerIndices,
  availableContainerSlots,
  parseContainersCsv,
} from "@/utils/shippingContainerSlots.js";
import {
  allocateProformCollectAppliedto,
  stage1SingleShipCollectLimits,
} from "@/components/gotrading/utils/stage1CollectAmountSplit.js";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

/** Valores por defecto del modal de cobro rápido (se puede sobreescribir vía prop `quickFieldKeys`). */
const DEFAULT_COLLECT_QUICK_KEYS = ["bankaccount", "file"];

function resolveCollectCollectionDateKey(modelFields) {
  if (!Array.isArray(modelFields)) return "collection_date";
  if (modelFields.some((f) => f && f.key === "collection_date")) return "collection_date";
  if (modelFields.some((f) => f && f.key === "date")) return "date";
  return "collection_date";
}

/** Redondeo de montos de cobro según decimales en Settings (p. ej. dot.comma.4). */
function roundCollectAmount(value) {
  return roundNumberBySettings(value);
}

/**
 * Mantiene la suma de montos en collect_appliedto alineada con `amount` del collect
 * (hook_validate_collect: el collect no puede ser menor al total aplicado).
 */
function syncAppliedtoAmountsWithCollectAmount(appliedto, collectAmount) {
  if (!Array.isArray(appliedto) || appliedto.length === 0) return appliedto;
  const target = roundCollectAmount(collectAmount);
  if (!Number.isFinite(target) || target < 0) return appliedto;

  const rows = appliedto.map((row) =>
    typeof row === "object" && row !== null ? { ...row } : row
  );

  const numericRows = rows.filter(
    (r) => typeof r === "object" && r !== null && "amount" in r
  );
  if (numericRows.length === 0) return appliedto;

  if (rows.length === 1 && typeof rows[0] === "object" && rows[0] !== null) {
    rows[0].amount = target;
    return rows;
  }

  const oldSum = numericRows.reduce(
    (s, row) => s + (parseFloat(row.amount) || 0),
    0
  );
  if (oldSum <= 0) {
    const each = roundCollectAmount(target / numericRows.length);
    let distributed = 0;
    for (let i = 0; i < numericRows.length - 1; i++) {
      numericRows[i].amount = each;
      distributed += each;
    }
    numericRows[numericRows.length - 1].amount = roundCollectAmount(
      target - distributed
    );
    return rows;
  }

  const ratio = target / oldSum;
  let acc = 0;
  for (let i = 0; i < numericRows.length - 1; i++) {
    const prev = parseFloat(numericRows[i].amount) || 0;
    const v = roundCollectAmount(prev * ratio);
    numericRows[i].amount = v;
    acc += v;
  }
  numericRows[numericRows.length - 1].amount = roundCollectAmount(target - acc);
  return rows;
}

/**
 * `processReferenceFormData` solo incluye subcampos del grupo `appliedto` que existen en ModelDefinition.
 * Rounding y bank_commission en collect_appliedto se reinyectan desde el merge previo al POST.
 */
function mergeCollectAppliedtoExtrasIntoPayload(mergedSource, payload) {
  if (
    !mergedSource ||
    !payload ||
    !Array.isArray(mergedSource.appliedto) ||
    !Array.isArray(payload.appliedto)
  ) {
    return;
  }
  const n = Math.min(mergedSource.appliedto.length, payload.appliedto.length);
  for (let i = 0; i < n; i++) {
    const src = mergedSource.appliedto[i];
    const dst = payload.appliedto[i];
    if (!src || !dst || typeof src !== "object" || typeof dst !== "object") continue;
    if (Object.prototype.hasOwnProperty.call(src, "rounding")) {
      const v = parseFloat(src.rounding);
      dst.rounding = Number.isFinite(v) ? v : 0;
    }
    if (Object.prototype.hasOwnProperty.call(src, "bank_commission")) {
      const v = parseFloat(src.bank_commission);
      dst.bank_commission = Number.isFinite(v) ? v : 0;
    }
  }
}

/** Líneas de grupo tipo factura (qty_containers): no persistir líneas en 0 al crear shipping rápido. */
function stripShippingGroupLinesWithZeroContainers(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const next = { ...payload };
  const keys = ["productandprice", "productandpriceshipper"];
  for (const key of keys) {
    const raw = next[key];
    if (raw == null) continue;
    if (Array.isArray(raw)) {
      const kept = raw.filter((line) => {
        if (!line || typeof line !== "object") return false;
        const q = Number(line.qty_containers);
        return Number.isFinite(q) && q > 0.005;
      });
      next[key] = kept.length ? kept : null;
    } else if (typeof raw === "object") {
      const q = Number(raw.qty_containers);
      if (!Number.isFinite(q) || q <= 0.005) {
        next[key] = null;
      }
    }
  }
  return next;
}

/** Limpia una línea de grupo (invoice/shipper) para alta embarque: sin _id anidados; product ref como id. */
function sanitizeGroupLineForEmbeddedCreate(line) {
  if (!line || typeof line !== "object") return {};
  let raw;
  try {
    raw = JSON.parse(JSON.stringify(line));
  } catch (e) {
    raw = { ...line };
  }
  const delMeta = (o) => {
    if (!o || typeof o !== "object") return;
    delete o._id;
    delete o.editHistory;
    delete o.createdAt;
    delete o.updatedAt;
    delete o.__v;
    delete o.idModel;
    delete o.createdBy;
    delete o.updatedBy;
  };
  delMeta(raw);
  if (Array.isArray(raw.products)) {
    raw.products = raw.products.map((p) => {
      const pr = typeof p === "object" && p !== null ? { ...p } : {};
      delMeta(pr);
      if (pr.product != null && typeof pr.product === "object" && pr.product._id != null) {
        pr.product = pr.product._id;
      }
      return pr;
    });
  }
  return raw;
}

/** Precios proveedor en 0 al crear desde quick shipping (se completan después en factura shipper). */
function zeroShipperProductRowPrices(line) {
  if (!line || typeof line !== "object") return line;
  if (Array.isArray(line.products)) {
    line.products = line.products.map((p) => {
      if (!p || typeof p !== "object") return p;
      const row = { ...p, unit_price: 0 };
      if (Object.prototype.hasOwnProperty.call(p, "unit_totalprice")) row.unit_totalprice = 0;
      if (Object.prototype.hasOwnProperty.call(p, "unit_pricetotal")) row.unit_pricetotal = 0;
      return row;
    });
  }
  if (Object.prototype.hasOwnProperty.call(line, "unit_price")) {
    line.unit_price = 0;
  }
  return line;
}

/**
 * Tras quitar líneas en 0: copiar invoice → supplier (mismas líneas, mismos productos, mismos qty_containers)
 * para cumplir hook_validate_shipping (misma cantidad de filas product en ambos grupos).
 */
function syncProductandpriceshipperFromInvoiceForQuickShipping(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const next = { ...payload };
  const inv = next.productandprice;
  if (inv == null) {
    next.productandpriceshipper = null;
    return next;
  }
  const lines = Array.isArray(inv) ? inv : [inv];
  const objects = lines.filter((l) => l && typeof l === "object");
  if (!objects.length) {
    next.productandpriceshipper = null;
    return next;
  }
  const invoiceSanitized = objects.map((line) => sanitizeGroupLineForEmbeddedCreate(line));
  next.productandprice = invoiceSanitized.length ? invoiceSanitized : null;
  next.productandpriceshipper = invoiceSanitized.length
    ? invoiceSanitized.map((line) =>
        zeroShipperProductRowPrices(
          sanitizeGroupLineForEmbeddedCreate(JSON.parse(JSON.stringify(line)))
        )
      )
    : null;
  return next;
}

function buildCollectFormDataFromFields(modelFields) {
  const newFormData = {};
  modelFields.forEach((field) => {
    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      newFormData[field.key] = field.defaultValue;
      return;
    }
    let defaultValue;
    switch (field.type) {
      case "array":
      case "group":
        defaultValue = field.isArray ? [] : {};
        break;
      case "boolean":
        defaultValue = false;
        break;
      case "number":
        defaultValue = 0;
        break;
      case "date":
        defaultValue = null;
        break;
      case "reference":
      case "list":
        defaultValue = field.isArray ? [] : null;
        break;
      default:
        defaultValue = "";
    }
    newFormData[field.key] = defaultValue;
  });
  return newFormData;
}

export default {
  name: "QuickAddCollectModal",
  components: { ContentSkeleton, Form2 },
  props: {
    isVisible: { type: Boolean, default: false },
    parentID: { type: String, default: null },
    parentModel: { type: String, default: null },
    extraParams: { type: Object, default: () => ({}) },
    lists: { type: Array, default: () => [] },
    /** Etapa 1 proforma: permitir elegir monto (≤ pendiente de adelanto). */
    allowAmountEdit: { type: Boolean, default: false },
    /** Tope sugerido / máximo (ej. remainingAmount etapa 1). */
    amountMax: { type: Number, default: null },
    /**
     * Etapa 1 proforma (FOB): si el monto supera amountMax y hay un solo embarque,
     * repartir entre bolsillo proforma y adelanto en ese embarque.
     */
    stage1AutoShipAdvanceSplit: { type: Object, default: null },
    /** Modelo dinámico destino (ej. collect, shipping). */
    targetModel: { type: String, default: "collect" },
    /** Subconjunto de keys del modelo que se muestran en el modal. */
    quickFieldKeys: {
      type: Array,
      default: () => [...DEFAULT_COLLECT_QUICK_KEYS],
    },
    modalTitleKey: {
      type: String,
      default: "financialSummary.quickAddCollectTitle",
    },
    /** Si se informa, sustituye el bloque de hints del cobro rápido. */
    modalHintKey: { type: String, default: null },
    missingFieldsMessageKey: {
      type: String,
      default: "financialSummary.quickAddCollectMissingFields",
    },
    successMessageKey: {
      type: String,
      default: "financialSummary.collectionCreatedSuccessfully",
    },
    /** Registro origen (p. ej. proforma) para mapeo → shipping al crear embarque rápido. */
    prefillSourceItem: { type: Object, default: null },
    /** Nombre del modelo origen del mapeo (p. ej. Proform). */
    prefillSourceModel: { type: String, default: "" },
    /** Solo cobro rápido (rayo): mostrar atajos de monto por contenedor / línea. */
    showContainerAmountCalculator: { type: Boolean, default: false },
    /**
     * Etapa 1 adelanto: % sobre la subtotal facturada de los contenedores elegidos (además de prefill.advance).
     */
    quickCollectAdvancePct: { type: Number, default: null },
    /**
     * Quick shipping: contenedores ya asignados por índice de línea en otros embarques (API balance proforma).
     */
    invoiceLineContainersAllocatedByIndex: {
      type: Array,
      default: () => [],
    },
    /** Embarques ya existentes (balance) para calcular slots libres de `containers` vs `qtycontainers`. */
    shippingRowsForContainerSlots: {
      type: Array,
      default: () => [],
    },
    /** Fila proforma del balance (opcional) para total de contenedores si falta en la proforma cargada. */
    balanceProformSnapshot: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      isSubmitting: false,
      modelFields: [],
      localVisualGroups: [],
      formData: {},
      models: [],
      modelDefinitions: {},
      /** `_id` de producto → nombre para líneas de factura (prefill + GET dynamic). */
      productDisplayNames: {},
      /** Quick collect: contenedores elegidos por línea de factura (índice = línea). */
      perLinePicks: [],
      /** Monto del cobro cuando hay calculadora (Form2 no incluye `amount`). */
      quickCollectAmount: 0,
      /** Monto inicial sugerido (pendiente) si no hay selección por línea. */
      quickCollectAmountBaseline: 0,
      /** Fecha de cobro (`collection_date` / `date` del modelo collect). */
      collectionDateDraft: "",
    };
  },
  computed: {
    isCollectTarget() {
      return String(this.targetModel || "collect").toLowerCase() === "collect";
    },
    modalHeaderIconClass() {
      const m = String(this.targetModel || "").toLowerCase();
      if (m === "shipping") return "fas fa-truck";
      return "fas fa-hand-holding-usd";
    },
    collectAmountNumeric() {
      if (!this.isCollectTarget || this.loading) return null;
      const raw =
        this.formData &&
        Object.prototype.hasOwnProperty.call(this.formData, "amount")
          ? this.formData.amount
          : undefined;
      if (raw === null || raw === undefined || raw === "") return null;
      const n =
        typeof raw === "number"
          ? raw
          : parseFloat(String(raw).replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : null;
    },
    /** Monto calculado por defaultvalues (servidor); solo lectura en este modal. */
    formattedCollectAmount() {
      const n = this.collectAmountNumeric;
      if (n == null) return null;
      return formatNumberSync(n, null, true);
    },
    appliedtoExtraRoundingNumeric() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return 0;
      const n = Number(at[0].rounding);
      return Number.isFinite(n) ? n : 0;
    },
    collectAmountWithRoundingVisible() {
      return (
        this.collectAppliedtoExtrasVisible &&
        this.appliedtoExtraRoundingNumeric > 0 &&
        this.collectAmountNumeric != null
      );
    },
    formattedCollectAmountWithRounding() {
      const amt = this.collectAmountNumeric;
      if (amt == null) return null;
      const total = roundCollectAmount(amt + this.appliedtoExtraRoundingNumeric);
      return formatNumberSync(total, null, true);
    },
    collectAppliedtoExtrasVisible() {
      return (
        this.isCollectTarget &&
        !this.loading &&
        this.formData &&
        Array.isArray(this.formData.appliedto) &&
        this.formData.appliedto.length > 0
      );
    },
    appliedtoExtraRoundingDisplay() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return "";
      const v = at[0].rounding;
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? n : "";
    },
    appliedtoExtraCommissionDisplay() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return "";
      const v = at[0].bank_commission;
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? n : "";
    },
    collectBankAccountTenantLabel() {
      const raw = String(getCurrentClientDbName() || "").trim();
      if (!raw) return "";
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    },
    quickFields() {
      if (!this.modelFields || !this.modelFields.length) return [];
      const tenant = this.collectBankAccountTenantLabel;
      const bankAccountLabel = tenant
        ? this.$t("financialSummary.quickAddCollectBankAccountLabel", { tenant })
        : this.$t("Bank account (to)");
      const labelKeyMap = {
        bankaccount: bankAccountLabel,
        comment: "Comment",
        file: "File",
        shipper: "Shipper",
        qtycontainers: "Containers",
      };
      const baseKeys = Array.isArray(this.quickFieldKeys)
        ? [...this.quickFieldKeys]
        : [...DEFAULT_COLLECT_QUICK_KEYS];
      let keys = baseKeys;
      if (this.collectAmountCalculatorVisible) {
        // Calculadora + banco + comentario; amount en la tarjeta; file/type/appliedto en formData (hook).
        keys = ["bankaccount"];
      } else if (
        this.allowAmountEdit &&
        this.modelFields.some((f) => f.key === "amount")
      ) {
        keys = ["amount", ...keys];
      }
      return keys
        .map((key) => {
          let field = this.modelFields.find((f) => f.key === key);
          if (!field) {
            if (key === "containers" && this.isShippingQuickWithPrefill) {
              field = {
                key: "containers",
                type: "string",
                label: "Containers involved",
                readonly: false,
                required: false,
                hide: false,
              };
            } else {
              return null;
            }
          }
          let overrides = {};
          if (key === "bankaccount") {
            overrides = { required: true, readonly: false };
          }
          if (key === "shipper") {
            overrides = { required: true, readonly: false };
          }
          if (key === "qtycontainers") {
            overrides = { required: true, readonly: false };
          }
          if (key === "containers") {
            overrides = { ...overrides, readonly: false, required: false };
          }
          if (key === "amount" && this.allowAmountEdit) {
            overrides = {
              ...overrides,
              readonly: false,
              required: true,
            };
            const cap = Number(this.resolvedAmountMaxForCollect);
            if (Number.isFinite(cap) && cap > 0) {
              overrides.max = cap;
            }
          }
          let label =
            key === "amount"
              ? this.$t("financialSummary.quickAddCollectAmountLabel")
              : key === "bankaccount"
                ? bankAccountLabel
                : key === "shipper"
                  ? this.$t("financialSummary.quickAddShippingShipperFieldLabel")
                  : key === "qtycontainers"
                    ? this.$t("financialSummary.quickAddShippingQtyContainersLabel")
                    : key === "containers"
                      ? this.$t("financialSummary.quickAddShippingContainersFieldLabel")
                      : labelKeyMap[key] || field.label;
          return {
            ...field,
            ...overrides,
            label,
          };
        })
        .filter(Boolean);
    },
    isShippingQuickWithPrefill() {
      return (
        String(this.targetModel || "").toLowerCase() === "shipping" &&
        !!this.prefillSourceItem
      );
    },
    /** Líneas factura proforma (origen) para tope de contenedores por fila en quick shipping. */
    proformLinesForShippingQtyCaps() {
      if (!this.isShippingQuickWithPrefill || !this.prefillSourceItem) return [];
      const src = this.prefillSourceItem;
      const raw =
        src.productandprice != null
          ? src.productandprice
          : src.group_proform && src.group_proform.productandprice != null
            ? src.group_proform.productandprice
            : null;
      return normalizeProductandpriceLines(raw);
    },
    shippingProductandpriceLines() {
      if (!this.isShippingQuickWithPrefill) return [];
      const raw = this.formData && this.formData.productandprice;
      if (raw == null) return [];
      const arr = Array.isArray(raw) ? raw : [raw];
      return arr.filter((x) => x && typeof x === "object");
    },
    showShippingInvoiceLinesEditor() {
      return this.shippingProductandpriceLines.length > 0;
    },
    shippingQuickQtycontainers() {
      const n = Number(this.formData && this.formData.qtycontainers);
      if (!Number.isFinite(n)) return null;
      return Math.round(n);
    },
    invoiceQtySum() {
      return this.shippingProductandpriceLines.reduce((s, line) => {
        const q = Math.round(Number(line && line.qty_containers));
        return s + (Number.isFinite(q) ? q : 0);
      }, 0);
    },
    invoiceQtySumMatches() {
      if (!this.showShippingInvoiceLinesEditor) return true;
      const q = this.shippingQuickQtycontainers;
      if (q == null || q < 1) return true;
      return this.invoiceQtySum === q;
    },
    canRedistributeInvoiceLineContainers() {
      if (!this.showShippingInvoiceLinesEditor) return false;
      const q = this.shippingQuickQtycontainers;
      return Number.isFinite(q) && q >= 1 && this.shippingProductandpriceLines.length > 0;
    },
    modalDialogClass() {
      if (this.loading) return "modal-dialog modal-md modal-dialog-centered";
      if (
        (this.quickFields.length > 0 && this.showShippingInvoiceLinesEditor) ||
        this.collectAmountCalculatorVisible
      ) {
        return "modal-dialog modal-lg modal-dialog-centered";
      }
      return "modal-dialog modal-md modal-dialog-centered";
    },
    collectCalcLines() {
      if (!this.isCollectTarget || !this.allowAmountEdit || !this.prefillSourceItem) {
        return [];
      }
      const src = this.prefillSourceItem;
      const raw =
        src.productandprice != null
          ? src.productandprice
          : src.group_proform && src.group_proform.productandprice != null
            ? src.group_proform.productandprice
            : null;
      return normalizeProductandpriceLines(raw);
    },
    collectAmountCalculatorVisible() {
      return (
        this.showContainerAmountCalculator &&
        this.isCollectTarget &&
        this.allowAmountEdit &&
        !!this.prefillSourceItem &&
        this.collectCalcLines.length > 0
      );
    },
    collectLineMetas() {
      return this.collectCalcLines.map((line) => {
        const e = lineEconomics(line);
        return { ...e, line };
      });
    },
    perLineSuggestedRaw() {
      const metas = this.collectLineMetas.map((m) => ({ qty: m.qty, perContainer: m.perContainer }));
      return sumPerLinePicks(metas, this.perLinePicks);
    },
    collectCalcCapShort() {
      const cap = Number(this.resolvedAmountMaxForCollect);
      if (!Number.isFinite(cap) || cap <= 0) return "";
      const pct = Number(this.resolvedQuickCollectAdvancePct);
      if (
        this.collectCalcUsesAdvanceSliceOnSelection &&
        Number.isFinite(pct) &&
        pct > 0.005
      ) {
        return this.$t("financialSummary.quickAddCollectCalcCapHintAdvancePct", {
          pct: this.formatAdvancePctLabel(pct),
          max: "$" + formatNumberSync(cap, null, true),
        });
      }
      return this.$t("financialSummary.quickAddCollectCalcCapShort", {
        max: "$" + formatNumberSync(cap, null, true),
      });
    },
    /** % adelanto: prop explícita o `advance` en la proforma (prefill). */
    resolvedQuickCollectAdvancePct() {
      const fromProp = Number(this.quickCollectAdvancePct);
      if (Number.isFinite(fromProp) && fromProp > 0.005) return fromProp;
      const src = this.prefillSourceItem;
      if (src && typeof src === "object") {
        const a = Number(src.advance);
        if (Number.isFinite(a) && a > 0.005) return a;
      }
      return null;
    },
    collectCalcUsesAdvanceSliceOnSelection() {
      if (!this.collectAmountCalculatorVisible) return false;
      const t = String(
        this.extraParams && this.extraParams.type ? this.extraParams.type : ""
      ).toLowerCase();
      const pct = Number(this.resolvedQuickCollectAdvancePct);
      return t === "advance" && Number.isFinite(pct) && pct > 0.005;
    },
    /**
     * Monto del cobro antes de limitar por adelanto pendiente (bolsillo etapa 1).
     * Con adelanto: subtotal contenedores × pct/100. Sin modo adelanto: = subtotal contenedores.
     */
    collectCalcNominalCollectFromSelection() {
      const raw = Number(this.perLineSuggestedRaw);
      if (!Number.isFinite(raw) || raw <= 0.005) return 0;
      if (!this.collectCalcUsesAdvanceSliceOnSelection) {
        return roundCollectAmount(raw);
      }
      const pct = Number(this.resolvedQuickCollectAdvancePct);
      return roundCollectAmount((raw * pct) / 100);
    },
    collectCalcTotalPicks() {
      if (!Array.isArray(this.perLinePicks)) return 0;
      return this.perLinePicks.reduce(
        (s, n) => s + Math.max(0, Math.round(Number(n)) || 0),
        0
      );
    },
    /** Tope efectivo en etapa 1 con un embarque (adelanto + factura pendiente en ese embarque). */
    resolvedAmountMaxForCollect() {
      const cap = Number(this.amountMax);
      if (!Number.isFinite(cap) || cap <= 0) return this.amountMax;
      const limits = stage1SingleShipCollectLimits(cap, this.stage1AutoShipAdvanceSplit);
      if (limits.single && limits.max != null && limits.max > cap + 0.005) {
        return limits.max;
      }
      return this.amountMax;
    },
    collectCalcAmountCappedByBalance() {
      if (!this.collectAmountCalculatorVisible) return false;
      const cap = Number(this.resolvedAmountMaxForCollect);
      const nominal = Number(this.collectCalcNominalCollectFromSelection);
      if (this.collectCalcTotalPicks <= 0) return false;
      if (!Number.isFinite(cap) || cap <= 0) return false;
      if (!Number.isFinite(nominal) || nominal <= 0.005) return false;
      return nominal > cap + 0.005;
    },
    /** Cobro desde proforma (anticipo): reparto capped en appliedto, monto del cobro puede ser mayor. */
    shouldAllocateProformCollectAppliedto() {
      return (
        this.isCollectTarget &&
        String(this.parentModel || "").toLowerCase() === "proform" &&
        String(this.extraParams && this.extraParams.type ? this.extraParams.type : "").toLowerCase() ===
          "advance"
      );
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.open();
        }
      },
    },
    perLinePicks: {
      deep: true,
      handler() {
        this.syncAmountFromContainerPicks();
      },
    },
  },
  methods: {
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
      const modelDefinitions = {};
      for (const model of data) {
        modelDefinitions[model.modelName] = model;
      }
      this.modelDefinitions = modelDefinitions;
    },
    async open() {
      if (!this.parentID || !this.parentModel) return;
      this.loading = true;
      try {
        await this.fetchModels();
        const extraParamsString =
          this.extraParams && typeof this.extraParams === "object"
            ? Object.entries(this.extraParams)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join("&")
            : "";

        const defName = encodeURIComponent(String(this.targetModel || "collect"));
        const url = `/api/gotrading/model-definitions/${defName}?parentID=${this.parentID}${
          extraParamsString ? "&" + extraParamsString : ""
        }&parentModel=${this.parentModel}`;
        const { data } = await wencesApi.get(url);

        if (data && data._operationMessages && Array.isArray(data._operationMessages)) {
          for (const msg of data._operationMessages) {
            await Swal.fire({
              icon: msg.type === "success" ? "success" : "info",
              title:
                msg.type === "success"
                  ? this.$tn("success")
                  : this.$tn("financialSummary.quickAddCollectInfoTitle"),
              text: msg.message,
              confirmButtonText: this.$tn("financialSummary.quickAddCollectUnderstood"),
            });
          }
        }

        if (data.redirect) {
          this.$router.push(`/${data.redirect}`);
          this.close();
          return;
        }

        if (data && data.visualGroups) {
          this.localVisualGroups = data.visualGroups;
          this.modelFields = data.fields || [];
        } else {
          this.modelFields = [];
          this.localVisualGroups = [];
        }

        this.formData = buildCollectFormDataFromFields(this.modelFields);

        if (this.isCollectTarget) {
          const dateKey = resolveCollectCollectionDateKey(this.modelFields);
          const fromDefaults = toYyyyMmDdSync(this.formData[dateKey]);
          this.collectionDateDraft = fromDefaults || calendarTodayLocalYyyyMmDd();
          this.formData = { ...this.formData, [dateKey]: this.collectionDateDraft };
        } else {
          this.collectionDateDraft = "";
        }

        if (
          String(this.targetModel || "").toLowerCase() === "shipping" &&
          this.prefillSourceItem &&
          this.parentID
        ) {
          await this.applyShippingPrefillFromProform();
          this.clampShippingInvoiceLinesToProformCaps();
          this.syncQuickShippingContainersField();
        }

        if (
          this.allowAmountEdit &&
          this.modelFields.some((f) => f.key === "amount")
        ) {
          const capStage1 = Number(this.amountMax);
          const capEffective = Number(this.resolvedAmountMaxForCollect);
          const hookAmt = Number(this.formData.amount);
          if (Number.isFinite(capStage1) && capStage1 > 0) {
            const suggested =
              Number.isFinite(hookAmt) && hookAmt > 0
                ? Math.min(hookAmt, capEffective)
                : capStage1;
            const amt = roundCollectAmount(suggested);
            let next = { ...this.formData, amount: amt };
            next = this.mergeProformCollectAllocation(next);
            if (
              !this.shouldAllocateProformCollectAppliedto &&
              next.appliedto &&
              Array.isArray(next.appliedto)
            ) {
              next.appliedto = syncAppliedtoAmountsWithCollectAmount(next.appliedto, amt);
            }
            this.formData = next;
          } else if (
            Number.isFinite(hookAmt) &&
            hookAmt > 0 &&
            this.formData.appliedto &&
            Array.isArray(this.formData.appliedto) &&
            this.formData.appliedto.length > 0
          ) {
            this.formData = {
              ...this.formData,
              appliedto: syncAppliedtoAmountsWithCollectAmount(
                this.formData.appliedto,
                hookAmt
              ),
            };
          }
        }

        if (this.collectAmountCalculatorVisible) {
          const a = Number(this.formData.amount);
          const rounded = Number.isFinite(a) ? roundCollectAmount(a) : 0;
          this.quickCollectAmountBaseline = rounded;
          this.quickCollectAmount = rounded;
        } else {
          this.quickCollectAmountBaseline = 0;
          this.quickCollectAmount = 0;
        }
      } catch (e) {
        console.error("QuickAddCollectModal load:", e);
        const openFailKey =
          String(this.targetModel || "").toLowerCase() === "shipping"
            ? "financialSummary.failedToOpenAddShippingModal"
            : "financialSummary.failedToOpenCollectionModal";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn(openFailKey),
        });
        this.close();
      } finally {
        this.loading = false;
        this.$nextTick(async () => {
          const needProductNames =
            this.collectAmountCalculatorVisible ||
            (String(this.targetModel || "").toLowerCase() === "shipping" &&
              this.prefillSourceItem);
          if (needProductNames) {
            try {
              await this.hydrateInvoiceProductDisplayNames();
            } catch (e) {
              console.error("hydrateInvoiceProductDisplayNames", e);
            }
          }
          if (this.collectAmountCalculatorVisible) {
            this.resetQuickCollectCalcState();
          }
        });
      }
    },
    resetQuickCollectCalcState() {
      const lines = this.collectCalcLines;
      this.perLinePicks = lines.map(() => 0);
    },
    formatAdvancePctLabel(pct) {
      const n = Number(pct);
      if (!Number.isFinite(n) || n <= 0) return "—";
      const rounded = Math.round(n * 1000) / 1000;
      return String(rounded % 1 === 0 ? Math.round(rounded) : rounded);
    },
    /** Monto fuera de Form2: solo estado local + `formData` para appliedto. */
    syncAmountFromContainerPicks() {
      if (!this.collectAmountCalculatorVisible) return;
      const capStage1 = Number(this.amountMax);
      const capEffective = Number(this.resolvedAmountMaxForCollect);
      const pct = Number(this.resolvedQuickCollectAdvancePct);
      const useAdvanceSlice =
        String(this.extraParams && this.extraParams.type ? this.extraParams.type : "")
          .toLowerCase() === "advance" &&
        Number.isFinite(pct) &&
        pct > 0.005;

      let v = this.quickCollectAmountBaseline;
      const raw = this.perLineSuggestedRaw;

      if (Number.isFinite(raw) && raw > 0.005) {
        if (useAdvanceSlice) {
          v = roundCollectAmount((raw * pct) / 100);
        } else {
          v = roundCollectAmount(raw);
          if (Number.isFinite(capEffective) && capEffective > 0 && v > capEffective) {
            v = roundCollectAmount(capEffective);
          }
        }
      }
      v = roundCollectAmount(v);
      if (!Number.isFinite(v) || v < 0) v = 0;
      this.quickCollectAmount = v;
      let next = { ...this.formData, amount: v };
      next = this.mergeProformCollectAllocation(next);
      if (
        !this.shouldAllocateProformCollectAppliedto &&
        next.appliedto &&
        Array.isArray(next.appliedto) &&
        next.appliedto.length > 0
      ) {
        next.appliedto = syncAppliedtoAmountsWithCollectAmount(next.appliedto, v);
      }
      this.formData = next;
    },
    formatCalcMoney(n) {
      const v = Number(n);
      if (!Number.isFinite(v) || v < 0) {
        return "$" + formatNumberSync(0, null, true);
      }
      return "$" + formatNumberSync(roundCollectAmount(v), null, true);
    },
    /** Nombre legible de un renglón dentro de `line.products` (inline o cache por id). */
    resolveProductRowDisplayName(row) {
      if (!row || typeof row !== "object") return "";
      const p = row.product;
      if (p != null && typeof p === "object" && (p.name || p.label)) {
        const t = String(p.name || p.label || "").trim();
        if (t) return t;
      }
      const id = this.normalizeProductId(p);
      if (id) {
        const cached = this.productDisplayNames[id];
        if (cached && String(cached).trim()) return String(cached).trim();
      }
      return "";
    },
    /** Productos de la línea de factura (orden de aparición, sin duplicados por nombre). */
    invoiceLineProductNames(line) {
      const products = line && Array.isArray(line.products) ? line.products : [];
      const names = [];
      const seen = new Set();
      for (const row of products) {
        if (!row || row.product == null) continue;
        const t = this.resolveProductRowDisplayName(row);
        if (!t) continue;
        const k = t.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        names.push(t);
      }
      return names;
    },
    /** Texto principal en el selector por contenedor: todos los productos de la línea, o «Line N». */
    invoiceLineProductsJoined(line, idx) {
      const names = this.invoiceLineProductNames(line);
      if (names.length === 0) return this.defaultInvoiceLineTitle(idx);
      return names.join(" · ");
    },
    onPerLinePickInput(ev, idx, maxQty) {
      let v = parseInt(String((ev && ev.target && ev.target.value) || "0"), 10);
      if (!Number.isFinite(v) || v < 0) v = 0;
      const cap = Math.max(0, Math.round(Number(maxQty)) || 0);
      if (v > cap) v = cap;
      const next = [...(this.perLinePicks || [])];
      while (next.length <= idx) next.push(0);
      next[idx] = v;
      this.perLinePicks = next;
      /** El watch a veces no basta (orden de flush / Form2); forzar sync en el tick siguiente. */
      this.$nextTick(() => {
        this.syncAmountFromContainerPicks();
      });
    },
    defaultInvoiceLineTitle(idx) {
      return this.$t("financialSummary.quickAddShippingInvoiceLineDefault", { n: idx + 1 });
    },
    normalizeProductId(ref) {
      if (ref == null) return null;
      if (typeof ref === "string" && ref.trim()) return ref.trim();
      if (typeof ref === "object" && ref._id != null) return String(ref._id);
      return null;
    },
    seedProductNamesFromProformPrefill() {
      const src = this.prefillSourceItem;
      if (!src || !src.productandprice) return;
      const raw = src.productandprice;
      const arr = Array.isArray(raw) ? raw : [raw];
      for (const line of arr) {
        if (!line || typeof line !== "object") continue;
        const products = Array.isArray(line.products) ? line.products : [];
        for (const row of products) {
          if (!row || row.product == null) continue;
          const p = row.product;
          if (typeof p === "object" && p !== null) {
            const id = this.normalizeProductId(p);
            const nm = String(p.name || p.label || "").trim();
            if (id && nm) this.$set(this.productDisplayNames, id, nm);
          }
        }
      }
    },
    collectProductIdsFromProductandpriceLines(lines) {
      const ids = [];
      const arr = Array.isArray(lines) ? lines : [];
      for (const line of arr) {
        const products = line && Array.isArray(line.products) ? line.products : [];
        for (const row of products) {
          if (!row || row.product == null) continue;
          const p = row.product;
          const id = this.normalizeProductId(p);
          if (!id) continue;
          const hasInlineName =
            typeof p === "object" &&
            p !== null &&
            String(p.name || p.label || "").trim().length > 0;
          if (!hasInlineName) ids.push(id);
        }
      }
      return [...new Set(ids)];
    },
    async fetchProductDocumentName(idStr) {
      if (!idStr || !/^[a-f0-9]{24}$/i.test(String(idStr).trim())) return null;
      const id = String(idStr).trim();
      for (const modelName of ["product", "Product"]) {
        try {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${encodeURIComponent(modelName)}/${encodeURIComponent(id)}`
          );
          if (data && (data.name != null || data.title != null)) {
            const t = String(data.name != null ? data.name : data.title).trim();
            if (t) return t;
          }
        } catch (e) {
          /* siguiente nombre de modelo */
        }
      }
      return null;
    },
    async hydrateInvoiceProductDisplayNames() {
      this.productDisplayNames = {};
      this.seedProductNamesFromProformPrefill();
      const shipLines = this.shippingProductandpriceLines || [];
      const calcLines = this.collectCalcLines || [];
      const ids = [
        ...new Set([
          ...this.collectProductIdsFromProductandpriceLines(shipLines),
          ...this.collectProductIdsFromProductandpriceLines(calcLines),
        ]),
      ];
      await Promise.all(
        ids.map(async (id) => {
          if (this.productDisplayNames[id]) return;
          const nm = await this.fetchProductDocumentName(id);
          if (nm) this.$set(this.productDisplayNames, id, nm);
        })
      );
    },
    invoiceLineTitle(line, idx) {
      const products = line && Array.isArray(line.products) ? line.products : [];
      const first = products[0];
      if (first && first.product != null) {
        const p = first.product;
        if (typeof p === "object" && p !== null && (p.name || p.label)) {
          const t = String(p.name || p.label || "").trim();
          if (t) return t;
        }
        const id = this.normalizeProductId(p);
        if (id) {
          const cached = this.productDisplayNames[id];
          if (cached && String(cached).trim()) return String(cached).trim();
        }
      }
      return this.defaultInvoiceLineTitle(idx);
    },
    invoiceLineSubtitle(line) {
      const products = line && Array.isArray(line.products) ? line.products : [];
      const n = products.length;
      if (n > 1) {
        return this.$t("financialSummary.quickAddShippingInvoiceLineProductsCount", { count: n });
      }
      return "";
    },
    invoiceLineQtyDisplay(line) {
      const n = Math.round(Number(line && line.qty_containers));
      if (!Number.isFinite(n) || n < 0) return 0;
      return n;
    },
    /**
     * Máx. contenedores editables en esta línea: proforma − ya asignados en otros embarques (mismo índice de línea).
     */
    invoiceLineQtyMaxFromProform(idx) {
      const arr = this.proformLinesForShippingQtyCaps;
      const pl = arr[idx];
      if (!pl) return null;
      const proformCap = Math.round(Number(pl.qty_containers));
      if (!Number.isFinite(proformCap) || proformCap < 0) return null;

      let allocated = 0;
      const ext = this.invoiceLineContainersAllocatedByIndex;
      if (Array.isArray(ext) && ext.length > idx) {
        const a = Math.round(Number(ext[idx]));
        if (Number.isFinite(a) && a > 0) allocated = a;
      }

      const remaining = proformCap - allocated;
      return Math.max(0, remaining);
    },
    lineQtyInputMax(idx) {
      const m = this.invoiceLineQtyMaxFromProform(idx);
      if (m == null || !Number.isFinite(m)) return undefined;
      return m;
    },
    setAppliedtoRoundingAndCommission(partial) {
      if (!partial || typeof partial !== "object") return;
      const at = this.formData.appliedto;
      if (!Array.isArray(at) || !at.length) return;
      const first = at[0] && typeof at[0] === "object" ? { ...at[0] } : {};
      const rOk = Object.prototype.hasOwnProperty.call(partial, "rounding")
        ? parseFloat(Number(partial.rounding).toFixed(2)) || 0
        : parseFloat(first.rounding) || 0;
      const cOk = Object.prototype.hasOwnProperty.call(partial, "bank_commission")
        ? parseFloat(Number(partial.bank_commission).toFixed(2)) || 0
        : parseFloat(first.bank_commission) || 0;
      const next = at.map((row, i) => {
        const base = typeof row === "object" && row ? { ...row } : {};
        if (i === 0) {
          return { ...base, rounding: rOk, bank_commission: cOk };
        }
        return { ...base, rounding: 0, bank_commission: 0 };
      });
      this.$set(this.formData, "appliedto", next);
    },
    onAppliedtoExtraRoundingInput(ev) {
      const raw = (ev && ev.target && ev.target.value) || "";
      const n = raw === "" ? 0 : parseFloat(String(raw).replace(",", "."));
      this.setAppliedtoRoundingAndCommission({
        rounding: Number.isFinite(n) ? n : 0,
      });
    },
    onAppliedtoExtraCommissionInput(ev) {
      const raw = (ev && ev.target && ev.target.value) || "";
      const n = raw === "" ? 0 : parseFloat(String(raw).replace(",", "."));
      this.setAppliedtoRoundingAndCommission({
        bank_commission: Number.isFinite(n) ? n : 0,
      });
    },
    clampShippingInvoiceLinesToProformCaps() {
      if (!this.showShippingInvoiceLinesEditor) return;
      const raw = this.formData.productandprice;
      const arr = Array.isArray(raw) ? [...raw] : raw && typeof raw === "object" ? [{ ...raw }] : null;
      if (!arr || !arr.length) return;
      let changed = false;
      const next = arr.map((line, idx) => {
        const cap = this.invoiceLineQtyMaxFromProform(idx);
        if (cap == null) return line;
        let q = Math.round(Number(line.qty_containers));
        if (!Number.isFinite(q) || q < 0) q = 0;
        if (q > cap) {
          changed = true;
          return { ...line, qty_containers: cap };
        }
        return line;
      });
      if (changed) {
        this.$set(this.formData, "productandprice", next);
      }
    },
    onInvoiceLineQtyInput(ev, line, idx) {
      let v = parseInt(String(ev.target.value || "0"), 10);
      if (!Number.isFinite(v) || v < 0) v = 0;
      const cap = this.invoiceLineQtyMaxFromProform(idx);
      if (cap != null && Number.isFinite(cap) && v > cap) v = cap;
      this.$set(line, "qty_containers", v);
      const raw = this.formData.productandprice;
      if (Array.isArray(raw)) {
        this.$set(this.formData, "productandprice", [...raw]);
      }
    },
    redistributeInvoiceLineContainers() {
      const lines = this.shippingProductandpriceLines;
      if (!lines.length) return;
      const total = Math.round(Number(this.formData.qtycontainers));
      if (!Number.isFinite(total) || total < 1) {
        Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.quickAddShippingQtyContainersInvalidTitle"),
          text: this.$tn("financialSummary.quickAddShippingQtyContainersInvalidText"),
        });
        return;
      }
      const n = lines.length;
      const caps = lines.map((_, i) => {
        const c = this.invoiceLineQtyMaxFromProform(i);
        return Number.isFinite(c) && c >= 0 ? c : Number.MAX_SAFE_INTEGER;
      });
      const qty = Array(n).fill(0);
      let rem = total;
      const maxIter = Math.max(total * n, n * 50) + 200;
      let iter = 0;
      while (rem > 0 && iter < maxIter) {
        let placed = false;
        for (let i = 0; i < n && rem > 0; i++) {
          if (qty[i] < caps[i]) {
            qty[i]++;
            rem--;
            placed = true;
          }
        }
        if (!placed) break;
        iter++;
      }
      const raw = this.formData.productandprice;
      const arr = Array.isArray(raw) ? [...raw] : raw ? [{ ...raw }] : [];
      const next = arr.map((line, i) => ({
        ...line,
        qty_containers: qty[i] != null ? qty[i] : line.qty_containers,
      }));
      this.$set(this.formData, "productandprice", next);
    },
    onFormInput(val) {
      if (val && typeof val === "object") {
        let next = { ...this.formData, ...val };
        if (
          this.isCollectTarget &&
          this.allowAmountEdit &&
          next.appliedto &&
          Array.isArray(next.appliedto) &&
          next.appliedto.length > 0
        ) {
          const amt = Number(next.amount);
          if (Number.isFinite(amt) && amt >= 0) {
            if (this.shouldAllocateProformCollectAppliedto) {
              next = this.mergeProformCollectAllocation(next);
            } else {
              next = {
                ...next,
                appliedto: syncAppliedtoAmountsWithCollectAmount(next.appliedto, amt),
              };
            }
          }
        }
        this.formData = next;
        if (
          String(this.targetModel || "").toLowerCase() === "shipping" &&
          this.prefillSourceItem &&
          Object.prototype.hasOwnProperty.call(val, "qtycontainers")
        ) {
          this.$nextTick(() => this.syncQuickShippingContainersField());
        }
      } else {
        this.formData = {};
      }
    },
    /**
     * Rellena `containers` con los primeros cupos libres (misma lógica que al guardar),
     * para que el usuario lo vea y pueda editarlo antes del POST.
     */
    syncQuickShippingContainersField() {
      if (String(this.targetModel || "").toLowerCase() !== "shipping") return;
      if (!this.prefillSourceItem) return;
      const keys = Array.isArray(this.quickFieldKeys) ? this.quickFieldKeys : [];
      if (!keys.includes("containers")) return;
      const qty = Math.round(Number(this.formData && this.formData.qtycontainers));
      const totalSlots = Math.round(
        resolveProformContainerCountFromRecord(
          this.prefillSourceItem,
          this.balanceProformSnapshot || null
        )
      );
      if (!Number.isFinite(qty) || qty < 1 || !Number.isFinite(totalSlots) || totalSlots <= 0) {
        this.$set(this.formData, "containers", "");
        return;
      }
      const used = collectUsedContainerIndices(
        this.shippingRowsForContainerSlots || [],
        totalSlots
      );
      const avail = availableContainerSlots(totalSlots, used);
      const csv = avail.slice(0, qty).join(",");
      this.$set(this.formData, "containers", csv);
    },
    async applyShippingPrefillFromProform() {
      const srcItem = this.prefillSourceItem;
      const srcModel =
        (this.prefillSourceModel && String(this.prefillSourceModel).trim()) ||
        this.parentModel ||
        "Proform";
      if (!srcItem || !this.parentID) return;
      try {
        const { data: referenceMapping } = await wencesApi.get(
          "/api/gotrading/model-reference-mapping",
          {
            params: {
              sourceModel: srcModel,
              targetModel: "shipping",
              sourceRecordId: this.parentID,
            },
          }
        );
        if (!referenceMapping?.data?.length) {
          const nextEmpty = { ...this.formData, custom_proform: this.parentID };
          const totalSlotsEmpty = Math.round(
            resolveProformContainerCountFromRecord(
              this.prefillSourceItem,
              this.balanceProformSnapshot || null
            )
          );
          if (totalSlotsEmpty > 0) {
            delete nextEmpty.containers;
          }
          this.formData = nextEmpty;
          return;
        }
        const next = { ...this.formData };
        referenceMapping.data[0].mappings.forEach((mapping) => {
          if (srcItem[mapping.sourceField] == null) return;
          let value = srcItem[mapping.sourceField];
          if (Array.isArray(value)) {
            value = value.map((sub) => {
              const cleanItem = { ...sub };
              if (cleanItem.products && Array.isArray(cleanItem.products)) {
                cleanItem.products = cleanItem.products.map((product) => {
                  const cleanProduct = { ...product };
                  delete cleanProduct._id;
                  delete cleanProduct.editHistory;
                  delete cleanProduct.createdAt;
                  delete cleanProduct.updatedAt;
                  delete cleanProduct.__v;
                  return cleanProduct;
                });
              }
              delete cleanItem._id;
              delete cleanItem.editHistory;
              delete cleanItem.createdAt;
              delete cleanItem.updatedAt;
              delete cleanItem.__v;
              return cleanItem;
            });
          }
          next[mapping.targetField] = value;
        });
        next.custom_proform = this.parentID;
        const totalSlots = Math.round(
          resolveProformContainerCountFromRecord(
            this.prefillSourceItem,
            this.balanceProformSnapshot || null
          )
        );
        if (totalSlots > 0) {
          delete next.containers;
        }
        this.formData = next;
      } catch (e) {
        console.warn("applyShippingPrefillFromProform:", e);
        this.formData = { ...this.formData, custom_proform: this.parentID };
      }
    },
    quickRefOrListFilled(val) {
      if (val == null) return false;
      if (typeof val === "string") return val.trim().length > 0;
      if (typeof val === "object" && val._id) return true;
      return false;
    },
    bankAccountChosen() {
      const ba = this.formData.bankaccount;
      if (ba == null) return false;
      if (typeof ba === "string") return ba.trim().length > 0;
      if (typeof ba === "object" && ba._id) return true;
      return false;
    },
    collectQuickFormShowsFileField() {
      return (this.quickFields || []).some((f) => f && f.key === "file");
    },
    async confirmDuntowerCollectSaveWithoutFile(mergedPre) {
      if (!this.isCollectTarget) return true;
      return confirmDuntowerCollectSaveWithoutFile((key, params) => this.$t(key, params), mergedPre, {
        showsFileField: this.collectQuickFormShowsFileField(),
      });
    },
    formatCollectCapMoney(n) {
      return "$" + formatNumberSync(Number(n), null, true);
    },
    /**
     * Reparte appliedto sin superar cupos de proforma/embarque; el monto del cobro no se recorta.
     */
    mergeProformCollectAllocation(mergedPre) {
      if (!this.shouldAllocateProformCollectAppliedto) return mergedPre;
      const amt = Number(mergedPre.amount);
      if (!Number.isFinite(amt) || amt < 0.01) return mergedPre;
      const result = allocateProformCollectAppliedto(
        amt,
        Number(this.amountMax),
        mergedPre.appliedto,
        this.stage1AutoShipAdvanceSplit,
        this.parentID
      );
      if (!result.ok) return mergedPre;
      const next = {
        ...mergedPre,
        amount: result.amount,
        appliedto: result.appliedto,
      };
      if (result.unallocated > 0.005) {
        next._collectUnallocated = result.unallocated;
      } else {
        delete next._collectUnallocated;
      }
      return next;
    },
    async validateCollectAmountBeforeSave(mergedPre) {
      if (!this.allowAmountEdit) {
        return { ok: true, merged: mergedPre };
      }

      const amt = Number(mergedPre.amount);
      if (!Number.isFinite(amt) || amt < 0.01) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.quickAddCollectAmountInvalidTitle"),
          text: this.$tn("financialSummary.quickAddCollectAmountInvalidText"),
        });
        return { ok: false };
      }

      // Anticipo proforma puede superar el cupo (reparto / unallocated). Shipping/factura: tope duro.
      if (!this.shouldAllocateProformCollectAppliedto) {
        const cap = Number(this.resolvedAmountMaxForCollect);
        if (Number.isFinite(cap) && cap >= 0 && amt > cap + 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.quickAddCollectAmountTooMuchTitle"),
            text: this.$tn("financialSummary.quickAddCollectAmountTooMuchText", {
              max: this.formatCollectCapMoney(cap),
            }),
          });
          return { ok: false };
        }
      }

      const merged = this.mergeProformCollectAllocation(mergedPre);
      return {
        ok: true,
        merged,
        unallocated: Number(merged._collectUnallocated) || 0,
      };
    },
    async submit() {
      const fromChild =
        this.$refs.collectFieldsForm && this.$refs.collectFieldsForm.formData
          ? this.$refs.collectFieldsForm.formData
          : {};
      let mergedPre = { ...this.formData, ...fromChild };

      let collectUnallocated = 0;

      if (this.isCollectTarget) {
        const dateKey = resolveCollectCollectionDateKey(this.modelFields);
        const dateVal = (this.collectionDateDraft || "").trim() || null;
        mergedPre = { ...mergedPre, [dateKey]: dateVal };
        if (dateKey !== "collection_date") {
          mergedPre.collection_date = dateVal;
        }
      }

      if (this.isCollectTarget && this.collectAmountCalculatorVisible && this.allowAmountEdit) {
        const a = Number(this.quickCollectAmount);
        mergedPre = {
          ...mergedPre,
          amount: Number.isFinite(a) ? roundCollectAmount(a) : 0,
        };
        mergedPre = this.mergeProformCollectAllocation(mergedPre);
      }

      if (this.isCollectTarget) {
        if (!this.bankAccountChosen()) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.quickAddCollectBankRequiredTitle"),
            text: this.$tn("financialSummary.quickAddCollectBankRequiredText"),
          });
          return;
        }
        if (this.allowAmountEdit) {
          const amountCheck = await this.validateCollectAmountBeforeSave(mergedPre);
          if (!amountCheck.ok) return;
          mergedPre = amountCheck.merged;
          collectUnallocated = Number(amountCheck.unallocated) || 0;
        }
      } else {
        for (const f of this.quickFields) {
          if (!f || !f.required) continue;
          const v = mergedPre[f.key];
          let ok = this.quickRefOrListFilled(v);
          if (f.key === "qtycontainers") {
            const n = Number(v);
            ok = Number.isFinite(n) && n >= 1 && Number.isInteger(n);
          }
          if (!ok) {
            await Swal.fire({
              icon: "warning",
              title:
                f.key === "qtycontainers"
                  ? this.$tn("financialSummary.quickAddShippingQtyContainersInvalidTitle")
                  : this.$tn("financialSummary.quickAddGenericRequiredTitle"),
              text:
                f.key === "qtycontainers"
                  ? this.$tn("financialSummary.quickAddShippingQtyContainersInvalidText")
                  : this.$tn("financialSummary.quickAddGenericRequiredText"),
            });
            return;
          }
        }
        if (this.showShippingInvoiceLinesEditor && !this.invoiceQtySumMatches) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.quickAddShippingInvoiceLinesInvalidTitle"),
            text: this.$tn("financialSummary.quickAddShippingInvoiceLinesInvalidText", {
              sum: this.invoiceQtySum,
              ship: this.shippingQuickQtycontainers,
            }),
          });
          return;
        }
      }

      let collectForceSaveWithoutFile = false;
      if (this.isCollectTarget) {
        const fileEmptyBeforeSave = isCollectFormFileEmpty(mergedPre && mergedPre.file);
        const maySave = await this.confirmDuntowerCollectSaveWithoutFile(mergedPre);
        if (!maySave) return;
        if (fileEmptyBeforeSave) collectForceSaveWithoutFile = true;
      }

      if (this.isSubmitting) return;
      this.isSubmitting = true;
      let dataToSend = null;
      let collectUnallocatedNotice = collectUnallocated;
      try {
        let merged = { ...mergedPre };
        delete merged._collectUnallocated;
        if (
          this.isCollectTarget &&
          this.allowAmountEdit &&
          !this.shouldAllocateProformCollectAppliedto &&
          merged.appliedto &&
          Array.isArray(merged.appliedto) &&
          merged.appliedto.length > 0
        ) {
          const amt = Number(merged.amount);
          if (Number.isFinite(amt)) {
            merged = {
              ...merged,
              appliedto: syncAppliedtoAmountsWithCollectAmount(merged.appliedto, amt),
            };
          }
        }
        if (String(this.targetModel || "").toLowerCase() === "shipping") {
          merged = stripShippingGroupLinesWithZeroContainers(merged);
          merged = syncProductandpriceshipperFromInvoiceForQuickShipping(merged);
          const qty = Math.round(Number(merged.qtycontainers));
          const totalSlots = Math.round(
            resolveProformContainerCountFromRecord(
              this.prefillSourceItem,
              this.balanceProformSnapshot || null
            )
          );
          if (Number.isFinite(qty) && qty >= 1 && totalSlots > 0) {
            const used = collectUsedContainerIndices(
              this.shippingRowsForContainerSlots || [],
              totalSlots
            );
            const avail = availableContainerSlots(totalSlots, used);
            if (avail.length < qty) {
              await Swal.fire({
                icon: "warning",
                title: this.$tn("financialSummary.quickAddShippingContainersNotEnoughTitle"),
                text: this.$tn("financialSummary.quickAddShippingContainersNotEnoughText", {
                  needed: qty,
                  free: avail.length,
                  total: totalSlots,
                }),
              });
              this.isSubmitting = false;
              return;
            }
            const availSet = new Set(avail);
            const userCsv =
              merged.containers != null && merged.containers !== undefined
                ? String(merged.containers).trim()
                : "";
            const parsed = userCsv ? parseContainersCsv(userCsv, totalSlots) : [];
            const userOk =
              userCsv &&
              parsed.length === qty &&
              parsed.every((n) => availSet.has(n));
            if (userCsv && !userOk) {
              await Swal.fire({
                icon: "warning",
                title: this.$tn("financialSummary.quickAddShippingContainersCsvInvalidTitle"),
                text: this.$tn("financialSummary.quickAddShippingContainersCsvInvalidText", {
                  total: totalSlots,
                  qty,
                }),
              });
              this.isSubmitting = false;
              return;
            }
            merged.containers = userOk
              ? parsed.join(",")
              : avail.slice(0, qty).join(",");
          }
        }
        dataToSend = processReferenceFormData(
          merged,
          this.modelFields,
          this.localVisualGroups
        );
        if (String(this.targetModel || "collect").toLowerCase() === "collect") {
          mergeCollectAppliedtoExtrasIntoPayload(merged, dataToSend);
        }
        delete dataToSend.idModel;
        delete dataToSend.undefined;
        delete dataToSend.createdBy;
        delete dataToSend.updatedBy;

        removeReferenceGroupIds(dataToSend, this.modelFields);

        const tm = String(this.targetModel || "collect").toLowerCase();
        if (tm === "shipping") {
          if (this.parentID) {
            dataToSend.custom_proform = this.parentID;
          }
          delete dataToSend.parentID;
          delete dataToSend.parentModel;
        } else {
          dataToSend.parentID = this.parentID;
          dataToSend.parentModel = this.parentModel;
        }

        const endpointModel = encodeURIComponent(String(this.targetModel || "collect"));
        if (collectForceSaveWithoutFile) {
          dataToSend.forceSave = true;
        }
        const response = await wencesApi.post(
          `/api/gotrading/dynamic/${endpointModel}`,
          dataToSend
        );

        await Swal.fire({
          title: this.$tn(this.successMessageKey),
          text:
            collectUnallocatedNotice > 0.01
              ? this.$tn("financialSummary.quickAddCollectUnallocatedRemainder", {
                  amount: this.formatCollectCapMoney(collectUnallocatedNotice),
                })
              : undefined,
          icon: "success",
        });
        this.$emit("saved", response.data);
        this.close();
      } catch (error) {
        console.error("QuickAddCollectModal submit:", error);
        const errorData = error.response && error.response.data;
        if (dataToSend && isHookValidationError(errorData)) {
          const confirmed = await promptHookValidationForceSave(
            (key, params) => this.$t(key, params),
            errorData.message,
            { modelName: this.targetModel || "collect" }
          );
          if (confirmed) {
            this.isSubmitting = true;
            try {
              const retryPayload = { ...dataToSend, forceSave: true };
              const endpointModel = encodeURIComponent(String(this.targetModel || "collect"));
              const response = await wencesApi.post(
                `/api/gotrading/dynamic/${endpointModel}`,
                retryPayload
              );
              await Swal.fire({
                title: this.$tn(this.successMessageKey),
                text:
                  collectUnallocatedNotice > 0.01
                    ? this.$tn("financialSummary.quickAddCollectUnallocatedRemainder", {
                        amount: this.formatCollectCapMoney(collectUnallocatedNotice),
                      })
                    : undefined,
                icon: "success",
              });
              this.$emit("saved", response.data);
              this.close();
            } catch (retryErr) {
              console.error("QuickAddCollectModal forceSave:", retryErr);
              await showForceSaveRetryError(
                (key, params) => this.$t(key, params),
                retryErr
              );
            } finally {
              this.isSubmitting = false;
            }
            return;
          }
        }
        const failKey =
          String(this.targetModel || "").toLowerCase() === "shipping"
            ? "financialSummary.failedToSaveShipping"
            : "financialSummary.failedToSaveCollection";
        let errorMessage = this.$t(failKey);
        if (errorData) {
          if (errorData.error) errorMessage = errorData.error;
          else if (errorData.message) errorMessage = errorData.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: errorMessage,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    close() {
      this.formData = {};
      this.modelFields = [];
      this.localVisualGroups = [];
      this.productDisplayNames = {};
      this.perLinePicks = [];
      this.quickCollectAmount = 0;
      this.quickCollectAmountBaseline = 0;
      this.collectionDateDraft = "";
      this.loading = false;
      this.isSubmitting = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
.modal.show {
  display: block;
}

.quick-shipping-invoice-lines__qty {
  width: 4.75rem;
  min-width: 4rem;
  text-align: right;
}
.quick-shipping-invoice-lines__list .quick-shipping-invoice-lines__row:last-child {
  border-bottom: none !important;
}

/* El popup es angosto: los anchos por defecto de Form2 (col-lg-3) dejan tres
   campos por fila y las etiquetas se parten en varias lineas. Aca los campos
   van uno abajo del otro, a todo el ancho del modal. */
.quick-collect-fields :deep(.form2-field-row) {
  padding: 0 !important;
}
.quick-collect-fields :deep(.form2-field-row) > [class*="col-"] {
  flex: 0 0 100%;
  max-width: 100%;
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
